import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import {
  ArrowLeft, ArrowRight, KeyRound, CheckCircle2, Shield, Users,
  LogIn, UserMinus, Lock, Eye, Fingerprint,
} from "lucide-react";
import { motion } from "framer-motion";
import keeperLogo from "@/assets/logo-keeper.png";
import keeperHeroBg from "@/assets/keeper-hero-bg.jpg";

const features = [
  "Centraal wachtwoordbeheer voor je hele organisatie",
  "Single Sign-On (SSO) via Microsoft — één keer inloggen, overal toegang",
  "Veilige kluis per medewerker voor wachtwoorden en geheime notities",
  "Gedeelde mappen voor teamwachtwoorden (bijv. social media, leveranciers)",
  "Automatisch wachtwoorden invullen via browserextensie en app",
  "Sterke wachtwoorden genereren met één klik",
  "Tweestapsverificatie (2FA) voor extra beveiliging",
  "Volledige audit-trail: wie heeft wat wanneer bekeken",
  "Veilige on- en offboarding van medewerkers",
  "Werkt op desktop, mobiel en in de browser",
];

const highlights = [
  {
    icon: LogIn,
    title: "SSO met Microsoft",
    text: "Medewerkers loggen in met hun Microsoft-account. Geen apart wachtwoord voor de wachtwoordmanager — dat scheelt gedoe en is veiliger.",
    color: "bg-hero-gradient",
  },
  {
    icon: Users,
    title: "Nieuwe collega? In een paar klikken klaar",
    text: "Voeg een medewerker toe en geef direct toegang tot de juiste wachtwoorden. Geen e-mails met wachtwoorden meer rondsturen.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: UserMinus,
    title: "Vertrek? Direct afgesloten",
    text: "Wanneer een medewerker vertrekt, trek je in één handeling alle toegang in. Geen risico op achtergebleven wachtwoorden.",
    color: "bg-rinqel-green-gradient",
  },
  {
    icon: Lock,
    title: "Gedeelde wachtwoorden, veilig geregeld",
    text: "Social media-accounts, leveranciersportalen of gedeelde tools: deel wachtwoorden veilig via gedeelde mappen in de kluis.",
    color: "bg-hero-gradient",
  },
  {
    icon: Eye,
    title: "Wie heeft wat bekeken?",
    text: "Met de volledige audit-trail zie je precies wie wanneer welk wachtwoord heeft bekeken of gewijzigd. Volledig transparant.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Fingerprint,
    title: "Zero-knowledge beveiliging",
    text: "Keeper kan je wachtwoorden niet inzien. Alles is versleuteld en alleen toegankelijk voor jou en je team.",
    color: "bg-rinqel-green-gradient",
  },
];

const WerkplekKeeper = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden bg-black">
        <img loading="eager" decoding="async" fetchPriority="high" src={keeperHeroBg} alt="Keeper wachtwoordbeheer: veilig wachtwoorden delen en opslaan" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="container relative z-10 flex min-h-[420px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                Optionele dienst · Keeper Enterprise
              </span>
            </motion.div>

            <motion.div
              className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 p-2"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <img loading="lazy" decoding="async" src={keeperLogo} alt="Keeper logo" className="h-full w-full object-contain shrink-0" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Wachtwoordmanager{" "}
              <span className="text-white/90">Keeper</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Centraal wachtwoordbeheer voor je hele organisatie. Veilig, overzichtelijk en naadloos gekoppeld aan Microsoft 365.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Waarom een <span className="text-gradient">wachtwoordmanager?</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Wachtwoorden op plakbriefjes, in Excel of steeds hetzelfde wachtwoord overal? Herkenbaar, maar niet veilig.
              Met Keeper bewaar je alle wachtwoorden in een versleutelde kluis. Medewerkers loggen gewoon in met hun
              Microsoft-account — geen extra gebruikersnamen of wachtwoorden om te onthouden. Handig, veilig en snel!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Wat kun je ermee?
          </motion.h2>

          <div className="mx-auto max-w-2xl">
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card">
              <ul className="space-y-3">
                {features.map((f, i) => (
                  <motion.li
                    key={f}
                    className="flex items-start gap-3 text-sm"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.04 }}
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                    <span>{f}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Veilig en{" "}
            <span className="text-gradient">praktisch</span>
          </motion.h2>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
            {highlights.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Waarom Enterprise */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Waarom de <span className="text-gradient">Enterprise-versie?</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Wij kiezen bewust voor Keeper Enterprise. Waarom? Omdat deze versie Single Sign-On (SSO) met Microsoft ondersteunt.
              Dat betekent dat medewerkers automatisch toegang krijgen wanneer ze inloggen bij Microsoft — en automatisch worden
              afgesloten wanneer ze vertrekken. Dat maakt het beheer eenvoudiger en de veiligheid een stuk beter.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Wat kost het?
            </h2>
            <div className="mt-6 rounded-2xl border border-border bg-card p-8 shadow-card">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-3xl font-bold">€ 6,00</span>
                <span className="text-muted-foreground">per gebruiker per maand</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                Inclusief Keeper Enterprise met SSO-koppeling aan Microsoft 365.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12">
        <div className="container">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="werkplek-keeper-cta">
                Meer weten? Neem contact op
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <Link to="/diensten/werkplek">Terug naar overzicht</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default WerkplekKeeper;
